import React, { useState, useEffect } from "react";
import QRCode from "qrcode";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, Mail, Loader2 } from "lucide-react";
import { apiCall } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

const TwoFactorSettings = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // TOTP setup flow
  const [setupSecret, setSetupSecret] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);

  // password prompts
  const [emailPwd, setEmailPwd] = useState("");
  const [disablePwd, setDisablePwd] = useState("");

  // recovery codes
  const [recoveryPwd, setRecoveryPwd] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const s = await apiCall("/auth/2fa/status");
      setStatus(s);
    } catch (e) {
      toast.error("Impossible de charger l'état 2FA.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadStatus();
      setSetupSecret(""); setQrDataUrl(""); setTotpCode(""); setEmailPwd(""); setDisablePwd("");
      setRecoveryPwd(""); setRecoveryCodes(null);
    }
  }, [open]);

  const startTotpSetup = async () => {
    setBusy(true);
    try {
      const r = await apiCall("/auth/2fa/totp/setup", { method: "POST" });
      setSetupSecret(r.secret);
      const url = await QRCode.toDataURL(r.otpauth_uri, { width: 220, margin: 1 });
      setQrDataUrl(url);
    } catch (e) {
      toast.error("Impossible de démarrer la configuration.");
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async () => {
    setBusy(true);
    try {
      await apiCall("/auth/2fa/totp/enable", { method: "POST", data: { code: totpCode.trim() } });
      toast.success("Application d'authentification activée ✔");
      setSetupSecret(""); setQrDataUrl(""); setTotpCode("");
      await loadStatus();
    } catch (e) {
      toast.error(e?.detail || "Code incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async () => {
    if (disablePwd.trim().length < 1) { toast.error("Saisissez votre mot de passe."); return; }
    setBusy(true);
    try {
      await apiCall("/auth/2fa/totp/disable", { method: "POST", data: { password: disablePwd } });
      toast.success("Application d'authentification désactivée.");
      setDisablePwd("");
      await loadStatus();
    } catch (e) {
      toast.error(e?.detail || "Mot de passe incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const toggleEmail = async (enabled) => {
    if (emailPwd.trim().length < 1) { toast.error("Saisissez votre mot de passe pour modifier la 2FA e-mail."); return; }
    setBusy(true);
    try {
      await apiCall("/auth/2fa/email/toggle", { method: "POST", data: { enabled, password: emailPwd } });
      toast.success(enabled ? "2FA par e-mail activée ✔" : "2FA par e-mail désactivée.");
      setEmailPwd("");
      await loadStatus();
    } catch (e) {
      toast.error(e?.detail || "Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  const forgetDevices = async () => {
    setBusy(true);
    try {
      const r = await apiCall("/auth/2fa/forget-devices", { method: "POST" });
      toast.success(`${r.removed || 0} appareil(s) de confiance oublié(s). La 2FA sera redemandée.`);
    } catch (e) {
      toast.error("Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  const generateRecovery = async () => {
    if (recoveryPwd.trim().length < 1) { toast.error("Saisissez votre mot de passe."); return; }
    setBusy(true);
    try {
      const r = await apiCall("/auth/2fa/recovery/generate", { method: "POST", data: { password: recoveryPwd } });
      setRecoveryCodes(r.codes || []);
      setRecoveryPwd("");
      toast.success("Codes de secours générés. Conservez-les précieusement !");
      await loadStatus();
    } catch (e) {
      toast.error(e?.detail || "Mot de passe incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const downloadRecovery = () => {
    if (!recoveryCodes) return;
    const blob = new Blob([`Racinefit — Codes de secours 2FA\nGénérés le ${new Date().toLocaleString()}\n\n${recoveryCodes.join("\n")}\n\nChaque code n'est utilisable qu'une seule fois.`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "racinefit-codes-secours.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto" data-testid="twofa-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-orange-600" /> {t("Sécurité — Double authentification (2FA)")}
          </DialogTitle>
          <DialogDescription>
            {t("Ajoutez une étape de vérification à votre connexion. Le mode hors-ligne reste disponible sur cet appareil.")}
          </DialogDescription>
        </DialogHeader>

        {loading || !status ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>
        ) : (
          <div className="space-y-6">
            {status.mandatory && (
              <div className="text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded p-2" data-testid="twofa-mandatory-note">{t("La 2FA est obligatoire pour les comptes administrateur. Un code par e-mail est toujours disponible comme secours.")}</div>
            )}

            {/* TOTP */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-medium"><Smartphone className="w-4 h-4" /> Application d'authentification (TOTP)</div>
                <Badge variant={status.totp_enabled ? "default" : "secondary"} data-testid="twofa-totp-status">
                  {status.totp_enabled ? "Activée" : "Désactivée"}
                </Badge>
              </div>
              <p className="text-xs text-gray-500">Google Authenticator, Authy, Microsoft Authenticator…</p>

              {!status.totp_enabled && !setupSecret && (
                <Button size="sm" onClick={startTotpSetup} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="twofa-totp-setup-btn">
                  {busy ? "…" : "Configurer"}
                </Button>
              )}

              {!status.totp_enabled && setupSecret && (
                <div className="space-y-3" data-testid="twofa-totp-setup-panel">
                  {qrDataUrl && <img src={qrDataUrl} alt="QR code 2FA" className="mx-auto border rounded" data-testid="twofa-qr" />}
                  <p className="text-xs text-gray-500 text-center break-all">Ou saisissez la clé : <code className="font-mono">{setupSecret}</code></p>
                  <div className="space-y-2">
                    <Label htmlFor="totp-code">Code à 6 chiffres de l'application</Label>
                    <Input id="totp-code" inputMode="numeric" maxLength={6} value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))} placeholder="123456"
                      data-testid="twofa-totp-code-input" />
                  </div>
                  <Button size="sm" onClick={confirmTotp} disabled={busy || totpCode.length !== 6} className="bg-orange-600 hover:bg-orange-700" data-testid="twofa-totp-enable-btn">
                    {busy ? "…" : "Activer"}
                  </Button>
                </div>
              )}

              {status.totp_enabled && (
                <div className="space-y-2">
                  <Label htmlFor="disable-pwd">Mot de passe pour désactiver</Label>
                  <Input id="disable-pwd" type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                    placeholder="Votre mot de passe" data-testid="twofa-totp-disable-pwd" />
                  <Button size="sm" variant="destructive" onClick={disableTotp} disabled={busy} data-testid="twofa-totp-disable-btn">
                    Désactiver l'application
                  </Button>
                </div>
              )}
            </div>

            {/* Email 2FA */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-medium"><Mail className="w-4 h-4" /> Code par e-mail</div>
                <Badge variant={status.email_2fa_enabled ? "default" : "secondary"} data-testid="twofa-email-status">
                  {status.email_2fa_enabled ? "Activé" : "Désactivé"}
                </Badge>
              </div>
              <p className="text-xs text-gray-500">{t("Un code sera envoyé à")} <span data-no-translate>{status.email || t("votre e-mail")}</span>.</p>
              {status.mandatory ? (
                <p className="text-xs text-orange-700">Toujours actif pour les administrateurs (non désactivable).</p>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="email-pwd">Mot de passe</Label>
                  <Input id="email-pwd" type="password" value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)}
                    placeholder="Votre mot de passe" data-testid="twofa-email-pwd" />
                  <div className="flex items-center gap-2">
                    <Switch checked={status.email_2fa_enabled} onCheckedChange={toggleEmail} disabled={busy || !status.has_email} data-testid="twofa-email-toggle" />
                    <span className="text-sm">{status.email_2fa_enabled ? "Activé" : "Désactivé"}</span>
                  </div>
                  {!status.has_email && <p className="text-xs text-red-600">Aucune adresse e-mail valide sur ce compte.</p>}
                </div>
              )}
            </div>

            {/* Recovery codes */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-medium">Codes de secours</div>
                <Badge variant={status.recovery_codes_remaining > 0 ? "default" : "secondary"} data-testid="twofa-recovery-count">
                  {status.recovery_codes_remaining || 0} restant(s)
                </Badge>
              </div>
              <p className="text-xs text-gray-500">Codes à usage unique pour vous connecter si vous perdez votre téléphone. Générer de nouveaux codes invalide les anciens.</p>

              {recoveryCodes ? (
                <div className="space-y-2" data-testid="twofa-recovery-list">
                  <div className="grid grid-cols-2 gap-2 bg-gray-50 border rounded p-3 font-mono text-sm">
                    {recoveryCodes.map((c) => (<div key={c} className="text-center tracking-wider">{c}</div>))}
                  </div>
                  <p className="text-xs text-orange-700">⚠️ Notez ces codes maintenant — ils ne seront plus affichés.</p>
                  <Button size="sm" variant="outline" onClick={downloadRecovery} data-testid="twofa-recovery-download">Télécharger (.txt)</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="recovery-pwd">Mot de passe</Label>
                  <Input id="recovery-pwd" type="password" value={recoveryPwd} onChange={(e) => setRecoveryPwd(e.target.value)}
                    placeholder="Votre mot de passe" data-testid="twofa-recovery-pwd" />
                  <Button size="sm" onClick={generateRecovery} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="twofa-recovery-generate-btn">
                    {status.recovery_codes_remaining > 0 ? "Régénérer les codes" : "Générer des codes"}
                  </Button>
                </div>
              )}
            </div>

            {/* Trusted devices */}
            <div className="border rounded-lg p-4 space-y-2">
              <div className="font-medium">Appareils de confiance</div>
              <p className="text-xs text-gray-500">Après une vérification 2FA réussie, cet appareil peut être mémorisé pendant 30 jours. Oubliez-les pour forcer une nouvelle vérification partout.</p>
              <Button size="sm" variant="outline" onClick={forgetDevices} disabled={busy} data-testid="twofa-forget-devices-btn">
                Oublier les appareils de confiance
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorSettings;
